/**
 * @fileoverview Reusable search field component with clear button and
 * customisable submit behaviour.
 */

import { Clear, Search } from '@mui/icons-material';
import { IconButton, TextField, type TextFieldProps } from '@mui/material';

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onClear?: () => void;
  placeholder?: string;
  autoFocus?: boolean;
  size?: TextFieldProps['size'];
  fullWidth?: boolean;
  label?: string;
  sx?: TextFieldProps['sx'];
}

export function SearchField({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = 'Search...',
  autoFocus = false,
  size = 'small',
  fullWidth = false,
  label,
  sx,
}: SearchFieldProps) {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (onSubmit) {
      onSubmit();
    }
  };

  const handleClear = () => {
    onChange('');
    if (onClear) {
      onClear();
    }
  };

  const content = (
    <TextField
      label={label}
      variant='outlined'
      size={size}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      fullWidth={fullWidth}
      autoFocus={autoFocus}
      slotProps={{
        input: {
          endAdornment: (
            <>
              {value && (
                <IconButton
                  aria-label='clear search'
                  size='small'
                  onClick={handleClear}
                  edge='end'
                >
                  <Clear fontSize='small' />
                </IconButton>
              )}
              {onSubmit && (
                <IconButton
                  type='submit'
                  aria-label='search'
                  size='small'
                  onClick={onSubmit}
                >
                  <Search fontSize='small' />
                </IconButton>
              )}
              {!onSubmit && (
                <Search fontSize='small' sx={{ mr: 1 }} />
              )}
            </>
          ),
          sx: {
            pr: 0.5,
            '& .MuiInputBase-input': {
              fontSize: '0.85rem',
              py: 0.5,
            },
            '& .MuiInputBase-input::placeholder': {
              fontSize: '0.85rem',
            },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: '32px',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderWidth: '1px',
        },
        ...sx,
        ...(label && {
          '& .MuiFormLabel-root': {
            fontSize: '0.85rem',
            mt: -0.335,
            '&.MuiInputLabel-shrink': {
              display: 'none',
            },
          },
          '& .MuiOutlinedInput-notchedOutline legend': {
            maxWidth: '0px',
          },
        }),
      }}
    />
  );

  if (onSubmit) {
    return <form onSubmit={handleSubmit}>{content}</form>;
  }

  return content;
}

